import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useSiteBranding } from "../contexts/SiteBrandingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const Register = ({ onLogin }) => {
  const { branding } = useSiteBranding();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get reCAPTCHA token
    const recaptchaToken = recaptchaRef.current?.getValue();
    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA verification");
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        name,
        email,
        password,
        recaptcha_token: recaptchaToken
      });

      // Check if admin (has access token) or regular user (pending approval)
      if (response.data.access_token) {
        // Admin registration - log in immediately
        onLogin(response.data.access_token, response.data.user);
        toast.success("Admin account created successfully!");
        navigate("/");
      } else {
        // Regular user - show pending approval message
        toast.success(response.data.message || "Registration successful! Please wait for admin approval.");
        navigate("/login");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
      // Reset reCAPTCHA
      recaptchaRef.current?.reset();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 py-12">
      <div className="w-full max-w-lg">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-48 h-48 rounded-3xl mb-8 shadow-2xl transform hover:scale-105 transition-transform duration-300" style={{ backgroundColor: branding.logo_bg_color || '#000000' }}>
            {branding.logo_url ? (
              <img 
                src={`${BACKEND_URL}${branding.logo_url}`} 
                alt="Logo" 
                className="w-full h-full object-contain rounded-3xl"
              />
            ) : (
              <span className="text-white text-5xl font-bold">ESMC</span>
            )}
          </div>
          <h1 className="text-5xl sm:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-black to-gray-700 mb-3">Create Account</h1>
          <p className="text-lg text-gray-600">ESMC Network</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-base font-semibold text-gray-700">
              Road Name
            </Label>
            <Input
              id="name"
              type="text"
              data-testid="register-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder=""
              required
              className="h-14 border-2 border-gray-400 focus:border-black transition-all text-base shadow-sm focus:shadow-md rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-base font-semibold text-gray-700">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="register-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              className="h-14 border-2 border-gray-400 focus:border-black transition-all text-base shadow-sm focus:shadow-md rounded-xl"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-base font-semibold text-gray-700">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              data-testid="register-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              className="h-14 border-2 border-gray-400 focus:border-black transition-all text-base shadow-sm focus:shadow-md rounded-xl"
            />
          </div>

          <div className="flex justify-center py-2">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              theme="light"
            />
          </div>

          <Button
            type="submit"
            data-testid="register-submit-button"
            disabled={loading}
            className="w-full h-16 bg-gradient-to-r from-black to-gray-900 text-white hover:from-gray-900 hover:to-black text-xl font-semibold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200 rounded-xl"
          >
            {loading ? "Creating account..." : "Create Account"}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-base text-gray-600">
            Already have an account?{" "}
            <Link to="/login" data-testid="login-link" className="text-black font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;